import { finance, financeKeys } from "@/services/finance";
import { Alert, Button, Skeleton } from "@mantine/core";
import { IconPlus, IconShoppingBag } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import classes from "@/modules/finance/finance.module.css";
import { DateField, FinanceContext, isoToday, monthStart, useAccounts } from "@/modules/finance/shared";
import Setup from "./Setup";
import Overview from "./Overview";
import Expenses from "./Expenses";
import Payroll from "./Payroll";
import Products from "./Products";
import Reports from "./Reports";

const TABS = [
  { key: "overview", label: "نمای کلی", element: Overview },
  { key: "expenses", label: "هزینه‌ها", element: Expenses, needs: "finance.view" },
  { key: "payroll", label: "حقوق و مساعده", element: Payroll, needs: "payroll.view" },
  { key: "products", label: "فروش محصول", element: Products, needs: "finance.view" },
  { key: "reports", label: "گزارش مالی", element: Reports, needs: "finance.view" },
] as const;

// «امور مالی»: one section with five destinations; the date range above applies to all but payroll,
// which has its own period.
export default function FinancePage() {
  const { tab = "overview" } = useParams();
  const navigate = useNavigate();
  const caps = useQuery({ queryKey: [...financeKeys.all, "capabilities"], queryFn: finance.capabilities, retry: false });
  const accounts = useAccounts();
  const [range, setRange] = useState({ from: monthStart(), to: isoToday() });
  const set = useMemo(() => new Set(caps.data?.capabilities ?? []), [caps.data]);
  const can = (c: Parameters<typeof set.has>[0]) => set.has(c);
  const state = useMemo(() => ({ range, setRange, can, ready: !!caps.data }), [range, caps.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const current = TABS.find((t) => t.key === tab);
  if (!current) return <Navigate to="/dashboard/finance/overview" replace />;

  if (caps.isLoading || accounts.isLoading)
    return <div className={classes.page}><Skeleton height={60} radius={14} /><Skeleton height={110} radius={14} /><Skeleton height={260} radius={14} /></div>;
  if (caps.isError || !can("finance.view"))
    return <div className={classes.page}><Alert color="gray" title="امور مالی">دسترسی به امور مالی این مجموعه را ندارید. مالک یا مدیر سالن می‌تواند این بخش را ببیند.</Alert></div>;

  const Tab = current.element;
  const today = isoToday();
  const preset = range.to === today ? (range.from === today ? "today" : range.from === monthStart() ? "month" : "custom") : "custom";
  const needsSetup = (accounts.data ?? []).length === 0;

  return (
    <FinanceContext.Provider value={state}>
      <div className={classes.page}>
        <div className={classes.head}>
          <div>
            <h1>امور مالی</h1>
            <p>هزینه‌ها، حقوق و فروش را یکجا پیگیری کنید. مبالغ به ریال است.</p>
          </div>
          {!needsSetup && (
            <div className={classes.headActions}>
              {can("expenses.write") && <Button leftSection={<IconPlus size={16} />} color="violet" onClick={() => navigate("/dashboard/finance/expenses?new=1")}>ثبت هزینه</Button>}
              {can("sales.write") && <Button variant="default" leftSection={<IconShoppingBag size={16} />} onClick={() => navigate("/dashboard/finance/products?sale=1")}>فروش محصول</Button>}
            </div>
          )}
        </div>

        {needsSetup ? (
          <Setup />
        ) : (
          <>
            <nav className={classes.tabs} aria-label="بخش‌های امور مالی">
              {TABS.filter((t) => !("needs" in t) || can(t.needs)).map((t) => (
                <Link key={t.key} to={`/dashboard/finance/${t.key}`} className={classes.tab} aria-current={t.key === tab ? "page" : undefined}>{t.label}</Link>
              ))}
            </nav>
            {tab !== "payroll" && (
              <div className={classes.toolbar}>
                <div role="group" aria-label="بازه">
                  <button type="button" className={classes.chip} aria-pressed={preset === "month"} onClick={() => setRange({ from: monthStart(), to: today })}>این ماه</button>
                  <button type="button" className={classes.chip} aria-pressed={preset === "today"} onClick={() => setRange({ from: today, to: today })}>امروز</button>
                </div>
                <div style={{ width: 150 }}><DateField label="از" value={range.from} max={range.to} onChange={(from) => from && setRange({ ...range, from })} /></div>
                <div style={{ width: 150 }}><DateField label="تا" value={range.to} max={today} onChange={(to) => to && setRange({ from: to < range.from ? to : range.from, to })} /></div>
              </div>
            )}
            <Tab />
          </>
        )}
      </div>
    </FinanceContext.Provider>
  );
}
